/* eslint-disable max-len */
import React from 'react';

import { Box, Container } from '@mui/material';

import SportsBasketballOutlinedIcon from '@mui/icons-material/SportsBasketballOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import classNames from 'classnames';
import AdsClickRoundedIcon from '@mui/icons-material/AdsClickRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import { Link } from 'react-router-dom';

import Formats from './composer/site-components/formats';
import VideoShowcase from './composer/site-components/video-showcase';

import styles from './composer/site-components/site.module.css';
import LogoGrid from './composer/site-components/logo-grid';

const Home = () => {
  return (
    <div>
      <Container className={styles.container}>
        <div className={classNames(styles.homeHeaderGrad, styles.headerOffset)}>
          <div className={styles.centerBlock}>
            <Box className={classNames(styles.header, styles.medium)}>Next gen video advertising.</Box>
            <Box className={classNames(styles.description, styles.medium)}>
              <span>Create new premium inventory for live events. </span>
              <span>Extend the reach and revenue potential of your content.</span>
            </Box>
          </div>
          <div className={styles.section}>
            <Box>
              <video
                className={styles.homeVideo}
                src="https://dxgcj7vu1u7nu.cloudfront.net/web-assets/HeroPIP.mp4"
                width="100%"
                height="100%"
                loop
                muted
                poster="https://dxgcj7vu1u7nu.cloudfront.net/web-assets/transmit-hero.jpg"
                autoPlay
                playsInline
              />
            </Box>
          </div>

          <div className={classNames(styles.mt12, styles.section)}>
            <LogoGrid />
          </div>
        </div>

        <div className={classNames(styles.sectionContainer, styles.mt12, styles.greyBg)}>
          <div className={classNames(styles.section, styles.mt6, styles.mb6)}>
            <VideoShowcase
              miniHeading="Stream Composer"
              bigHeading="Go beyond traditional ad breaks."
              desc="Expand advertising inventory, engage audiences and propel growth."
              defaultSrc="https://dxgcj7vu1u7nu.cloudfront.net/web-assets/nba-pip.mp4"
              defaultPoster="https://dxgcj7vu1u7nu.cloudfront.net/web-assets/nba-poster.jpg"
              content={[
                {
                  id: 'pip',
                  src: 'https://dxgcj7vu1u7nu.cloudfront.net/web-assets/nba-pip.mp4',
                  icon: <SportsBasketballOutlinedIcon className="icon" />,
                  buttonText: (
                    <>
                      <b>Viewing experience uninterrupted.</b> Display personalized creatives without commercial breaks.
                    </>
                  ),
                },
                {
                  id: 'reveal',
                  src: 'https://dxgcj7vu1u7nu.cloudfront.net/web-assets/yes-lbar.mp4',
                  icon: <AutoAwesomeOutlinedIcon className="icon" />,
                  buttonText: (
                    <>
                      <b>Spotlight pivotal moments.</b> Create inventory using real time in-game event analysis.
                    </>
                  ),
                },
                {
                  id: 'borderFrame',
                  src: 'https://dxgcj7vu1u7nu.cloudfront.net/web-assets/yes-border-frame.mp4',
                  icon: <EmojiEventsOutlinedIcon className="icon" />,
                  buttonText: (
                    <>
                      <b>Brand the experience.</b> Showcase brand assets during highest viewer engagement.
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>

        <div className={styles.section}>
          <Box className={styles.mt12}>
            <Box className={styles.centeredMessageHeading}>In-stream Inventory</Box>
            <Box className={styles.centeredMessageDesc}>
              Leverage unique creative formats and fill traditional pods more effectively.
            </Box>
            <Box className={styles.mt4}>
              <Formats />
            </Box>
          </Box>
        </div>

        <div className={classNames(styles.sectionContainer, styles.mt12, styles.greyBg)}>
          <div className={classNames(styles.section, styles.mt6, styles.mb6)}>
            <VideoShowcase
              miniHeading="Reach Extension"
              bigHeading="Boost your audience."
              desc="Unlock the monetization potential of your promotional content driving awareness alongside brand advertisers."
              defaultSrc="https://dxgcj7vu1u7nu.cloudfront.net/web-assets/inbreak-Fox.mp4"
              videoOnLeft
              content={[
                {
                  id: 'pip',
                  src: 'https://dxgcj7vu1u7nu.cloudfront.net/web-assets/hp-reach-grap-attention.mp4',
                  icon: <AdsClickRoundedIcon className="icon" />,
                  buttonText: (
                    <>
                      <b>Grab attention.</b> Power captivating content moments fueling business growth.
                    </>
                  ),
                },
                {
                  id: 'reveal',
                  src: 'https://dxgcj7vu1u7nu.cloudfront.net/web-assets/hp-reach-promote.mp4',
                  icon: <WorkspacePremiumRoundedIcon className="icon" />,
                  buttonText: (
                    <>
                      <b>Promote content.</b> Engage customers with their favorite brand advertisers.
                    </>
                  ),
                },
              ]}
            />
          </div>
        </div>

        <div className={classNames(styles.section, styles.mt12, styles.mb6)}>
          <h2 className={styles.h2}>Mix and match.</h2>
          <div className={classNames(styles.mb3, styles.font24)}>Choose what works best for you.</div>
          <div className={styles.featureGrid}>
            <div className={classNames(styles.featureBox, styles.ar1, styles.scExplore)}>
              <div className={styles.featureTitle}>Stream Composer</div>
              <div>Integrate personalized ads directly into your streaming content.</div>
              <Link to="/stream-composer">Explore Stream Composer</Link>
            </div>
            <div className={classNames(styles.featureBox, styles.ar1, styles.featureBoxDark, styles.csExplore)}>
              <div className={styles.featureTitle}>Creative Studio</div>
              <div>Build creatives for new in-stream ad formats.</div>
              <Link to="/creative-studio">Explore Creative Studio</Link>
            </div>
            <div className={classNames(styles.featureBox, styles.ar1, styles.featureBoxDark, styles.reExplore)}>
              <div className={styles.featureTitle}>Reach Extension</div>
              <div>Extend the reach and monetization potential of your content.</div>
              <Link to="/reach-extension">Explore Reach Extension</Link>
            </div>
            <div className={classNames(styles.featureBox, styles.ar1, styles.apiExplore)}>
              <div className={styles.featureTitle}>API</div>
              <div>Integrate Transmit within your 1st or 3rd party video streaming platform.</div>
              {/* <Link to="/api">Explore API</Link> */}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Home;
